import { useMemo } from 'react';
import { scenes } from '@padcanvas/shared';
import { useModules } from '@shared-ui/reference-data';

/** Every chapter a bench covers, in registry order. Routing is fixed by chapter,
 *  so this list is the whole of what the Scenes page can show. */
export const BENCH_MODULES: { moduleId: string; bench: scenes.BenchId }[] =
    scenes.allBenches().flatMap(b => b.covers.map(m => ({ moduleId: m, bench: b.bench })));

export const TRUSTS: scenes.Trust[] = ['curated', 'verified', 'unverified', 'rejected'];
export const CONFIDENCES: scenes.Confidence[] = ['high', 'medium', 'low'];
export const AGREEMENTS: scenes.Agreement[] = ['agrees', 'disagrees', 'no-key'];

export const TRUST_HINT: Record<scenes.Trust, string> = {
    curated: 'Checked and accepted by a reviewer',
    verified: 'Solves, and every ask with a key agrees',
    unverified: 'Solves, but the key is absent for some ask',
    rejected: 'Fails validation, disagrees with the key, or cannot',
};

export const CANNOT_LABEL: Record<scenes.CannotReason, string> = {
    'not-a-setup': 'Not a setup',
    'outside-bench': 'Outside the bench',
    'needs-unsupported-feature': 'Needs an unsupported feature',
    'setup-ambiguous': 'Setup ambiguous',
    'diagram-unreadable': 'Diagram unreadable',
};

export const AGREEMENT_MARK: Record<scenes.Agreement, string> = {
    agrees: '✓', disagrees: '✗', 'no-key': '–',
};

export function formatReading(value: scenes.Reading | undefined, unit?: string): string {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'number') return `${+value.toPrecision(4)}${unit ? ` ${unit}` : ''}`;
    return value;
}

/** "Parent → Chapter" for a module id, falling back to the id while modules load
 *  or when the module is not in the catalogue. */
export function useModuleLabel(): (moduleId: string) => string {
    const { modules } = useModules();
    return useMemo(() => {
        const byId = new Map(modules.map(m => [m.id, m]));
        return (moduleId: string) => {
            const m = byId.get(moduleId);
            if (!m) return moduleId;
            const parent = m.parentModuleId ? byId.get(m.parentModuleId) : undefined;
            return parent ? `${parent.displayName} → ${m.displayName}` : m.displayName;
        };
    }, [modules]);
}
